import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Bell, BellOff, Check, CheckCheck, Clock3, ExternalLink, Save, Send, ShieldCheck, Smartphone, X } from "lucide-react";
import { api, patch, post, remove } from "../lib/api";
import { dateTimeBr } from "../lib/format";
import { Field } from "../components/Form";
import { PageHeader, SectionCard } from "../components/Page";

type Preference = {
  payablesEnabled: boolean;
  receivablesEnabled: boolean;
  boletosEnabled: boolean;
  invoicesEnabled: boolean;
  commissionsEnabled: boolean;
  reconciliationEnabled: boolean;
  closingEnabled: boolean;
  budgetsEnabled: boolean;
  payableLeadDays: number;
  receivableLeadDays: number;
  boletoLeadDays: number;
};

type NotificationConfig = {
  push: { configured: boolean; publicKey: string };
  device: null | { id: string; deviceId: string; deviceName: string; active: boolean; createdAt: string; lastUsedAt: string };
  preferences: Preference;
};

type AlertItem = {
  id: string;
  title: string;
  body: string;
  route: string;
  tone: string;
  status: string;
  referenceDate: string;
  createdAt: string;
};

const preferenceRows: Array<{ key: keyof Preference; label: string; description: string }> = [
  { key: "payablesEnabled", label: "Contas a Pagar", description: "Vencimentos próximos, no dia e vencidos." },
  { key: "receivablesEnabled", label: "Contas a Receber", description: "Parcelas próximas, no dia e vencidas." },
  { key: "boletosEnabled", label: "Boletos", description: "Emissões programadas ainda pendentes." },
  { key: "invoicesEnabled", label: "Notas Fiscais", description: "Lembrete mensal somente quando houver NF a emitir." },
  { key: "commissionsEnabled", label: "Comissões", description: "Comissões liberadas, vencendo ou vencidas, sem duplicidade." },
  { key: "reconciliationEnabled", label: "Conciliação", description: "Pendências bancárias no fechamento mensal." },
  { key: "closingEnabled", label: "Fechamento", description: "Alerta consolidado das pendências financeiras." },
  { key: "budgetsEnabled", label: "Orçamento de Obras", description: "Mudança para 80% ou estouro projetado." }
];

function deviceId() {
  const key = "cce-push-device-id";
  let value = localStorage.getItem(key);
  if (!value) {
    value = crypto.randomUUID();
    localStorage.setItem(key, value);
  }
  return value;
}

function deviceName() {
  const agent = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(agent)) return "iPhone / iPad";
  if (/Android/i.test(agent)) return "Dispositivo Android";
  if (/Windows/i.test(agent)) return "Computador Windows";
  if (/Macintosh/i.test(agent)) return "Computador Apple";
  return "Navegador deste dispositivo";
}

function applicationServerKey(value: string) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - normalized.length % 4) % 4);
  const raw = atob(normalized + padding);
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

export function NotificationsSettingsPage({ notify }: { notify: (message: string, tone?: "success" | "error") => void }) {
  const [config, setConfig] = useState<NotificationConfig | null>(null);
  const [working, setWorking] = useState(false);
  const currentDeviceId = useMemo(deviceId, []);
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

  const load = useCallback(async () => {
    setConfig(await api<NotificationConfig>(`/notifications/config?deviceId=${encodeURIComponent(currentDeviceId)}`));
  }, [currentDeviceId]);
  useEffect(() => { void load().catch((error) => notify(error instanceof Error ? error.message : "Falha ao carregar notificações.", "error")); }, [load, notify]);

  const activate = async () => {
    if (!config?.push.configured) return notify("As chaves VAPID ainda não foram configuradas no servidor.", "error");
    if (!supported) return notify("Este navegador não oferece suporte a notificações Web Push.", "error");
    if (!window.isSecureContext) return notify("Notificações exigem HTTPS ou acesso por localhost.", "error");
    setWorking(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") throw new Error("A permissão de notificações não foi concedida neste dispositivo.");
      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      const subscription = existing || await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: applicationServerKey(config.push.publicKey) });
      await post("/notifications/subscriptions", {
        deviceId: currentDeviceId,
        deviceName: deviceName(),
        userAgent: navigator.userAgent,
        subscription: subscription.toJSON()
      });
      await load();
      notify("Notificações ativadas neste dispositivo.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Não foi possível ativar as notificações.", "error");
    } finally { setWorking(false); }
  };

  const deactivate = async () => {
    setWorking(true);
    try {
      await remove(`/notifications/subscriptions/${encodeURIComponent(currentDeviceId)}`);
      const registration = await navigator.serviceWorker?.getRegistration();
      await (await registration?.pushManager.getSubscription())?.unsubscribe();
      await load();
      notify("Notificações desativadas neste dispositivo.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Não foi possível desativar as notificações.", "error");
    } finally { setWorking(false); }
  };

  const savePreferences = async () => {
    if (!config) return;
    setWorking(true);
    try {
      const preferences = await patch<Preference>("/notifications/preferences", config.preferences);
      setConfig({ ...config, preferences });
      notify("Preferências de notificação atualizadas.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Não foi possível salvar as preferências.", "error");
    } finally { setWorking(false); }
  };

  const test = async () => {
    setWorking(true);
    try {
      await post("/notifications/test", { deviceId: currentDeviceId });
      notify("Notificação de teste enviada para este dispositivo.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Não foi possível enviar a notificação de teste.", "error");
    } finally { setWorking(false); }
  };

  if (!config) return <div className="page loading-page"><span className="spinner"/> Carregando notificações...</div>;
  const active = Boolean(config.device?.active);
  const changePreference = (key: keyof Preference, value: boolean | number) => setConfig({ ...config, preferences: { ...config.preferences, [key]: value } });
  return <div className="page notifications-page">
    <PageHeader eyebrow="Configurações → Notificações" title="Notificações" description="Alertas internos e Web Push por dispositivo, sem armazenar informações financeiras no cache offline." actions={<button className="button button-secondary" onClick={() => { location.hash = "alertas"; }}><Bell size={16}/> Central de Alertas</button>}/>
    <div className="notifications-settings-grid">
      <SectionCard title="Este dispositivo" description="A permissão só é solicitada quando você escolhe ativar.">
        <div className={`push-device-status ${active ? "active" : "inactive"}`}>
          <Smartphone size={26}/><div><span>Notificações</span><strong>{active ? "ATIVADAS ✓" : "DESATIVADAS"}</strong><small>{config.device?.deviceName || deviceName()}</small></div>
        </div>
        {!config.push.configured && <div className="info-box warning"><AlertTriangle size={18}/><span>O servidor está pronto, mas aguarda VAPID_PUBLIC_KEY, VAPID_PRIVATE_KEY e VAPID_SUBJECT.</span></div>}
        {!supported && <div className="info-box warning"><AlertTriangle size={18}/><span>Web Push não é suportado por este navegador. No iPhone, instale o PWA na Tela de Início antes de ativar.</span></div>}
        <div className="notification-device-actions">
          {!active ? <button className="button button-primary" disabled={working || !config.push.configured || !supported} onClick={() => void activate()}><Bell size={17}/> Ativar notificações neste dispositivo</button> : <button className="button button-danger" disabled={working} onClick={() => void deactivate()}><BellOff size={17}/> Desativar neste dispositivo</button>}
          <button className="button button-accent" disabled={working || !active} onClick={() => void test()}><Send size={17}/> Enviar notificação de teste</button>
        </div>
        <div className="info-box"><ShieldCheck size={18}/><span>CPF, CNPJ, conta bancária, senha e tokens nunca são incluídos nas mensagens push.</span></div>
      </SectionCard>
      <SectionCard title="Preferências por usuário" description="O processamento central ocorre uma vez por dia, pela manhã, em America/Sao_Paulo.">
        <div className="notification-preferences">
          {preferenceRows.map((row) => <label key={row.key} className="notification-preference-row"><input type="checkbox" checked={Boolean(config.preferences[row.key])} onChange={(event) => changePreference(row.key, event.target.checked)}/><span><strong>{row.label}</strong><small>{row.description}</small></span></label>)}
        </div>
        <div className="notification-leads">
          <Field label="Contas a Pagar — antecedência (dias)" type="number" min="0" max="30" value={config.preferences.payableLeadDays} onChange={(event) => changePreference("payableLeadDays", Number(event.target.value))}/>
          <Field label="Contas a Receber — antecedência (dias)" type="number" min="0" max="30" value={config.preferences.receivableLeadDays} onChange={(event) => changePreference("receivableLeadDays", Number(event.target.value))}/>
          <Field label="Boletos — antecedência (dias)" type="number" min="0" max="30" value={config.preferences.boletoLeadDays} onChange={(event) => changePreference("boletoLeadDays", Number(event.target.value))}/>
        </div>
        <div className="modal-actions"><button className="button button-primary" disabled={working} onClick={() => void savePreferences()}><Save size={17}/> Salvar preferências</button></div>
      </SectionCard>
    </div>
  </div>;
}

const scopes = [
  { id: "today", label: "Hoje", icon: Bell },
  { id: "upcoming", label: "Próximos", icon: Clock3 },
  { id: "overdue", label: "Vencidos", icon: AlertTriangle },
  { id: "completed", label: "Concluídos", icon: CheckCheck }
] as const;

export function AlertsCenterPage({ notify }: { notify: (message: string, tone?: "success" | "error") => void }) {
  const [scope, setScope] = useState<(typeof scopes)[number]["id"]>("today");
  const [items, setItems] = useState<AlertItem[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api<{ items: AlertItem[]; counts: Record<string, number> }>(`/notifications?scope=${scope}`);
      setItems(response.items); setCounts(response.counts);
    } finally { setLoading(false); }
  }, [scope]);
  useEffect(() => { void load().catch((error) => notify(error instanceof Error ? error.message : "Falha ao carregar alertas.", "error")); }, [load, notify]);

  const openAlert = async (item: AlertItem) => {
    await patch(`/notifications/${item.id}/read`, {}).catch(() => undefined);
    location.hash = item.route;
  };
  const dismiss = async (item: AlertItem) => {
    try { await patch(`/notifications/${item.id}/dismiss`, {}); await load(); }
    catch (error) { notify(error instanceof Error ? error.message : "Falha ao dispensar alerta.", "error"); }
  };

  return <div className="page notifications-page">
    <PageHeader eyebrow="Central de Alertas" title="Alertas e vencimentos" description="Acompanhe alertas criados, enviados, lidos e dispensados em um único lugar." actions={<button className="button button-secondary" onClick={() => { location.hash = "configuracoes/notificacoes"; }}><Smartphone size={16}/> Configurar notificações</button>}/>
    <div className="alerts-scope-tabs">{scopes.map((tab) => <button key={tab.id} className={scope === tab.id ? "active" : ""} onClick={() => setScope(tab.id)}><tab.icon size={16}/><span>{tab.label}</span><b>{counts[tab.id] || 0}</b></button>)}</div>
    <SectionCard title={scopes.find((tab) => tab.id === scope)?.label || "Alertas"} description="Toque em um alerta para abrir diretamente o registro correspondente.">
      {loading ? <div className="loading-inline"><span className="spinner"/> Atualizando alertas</div> : items.length ? <div className="alerts-center-list">{items.map((item) => <article key={item.id} className={item.tone.toLowerCase()}><div className="alert-center-icon">{item.status === "READ" ? <Check size={18}/> : <AlertTriangle size={18}/>}</div><button className="alert-center-main" onClick={() => void openAlert(item)}><strong>{item.title}</strong><span>{item.body}</span><small>Referência {item.referenceDate.split("-").reverse().join("/")} · criado em {dateTimeBr(item.createdAt)}</small></button><div className="alert-center-actions"><button className="button button-small button-secondary" onClick={() => void openAlert(item)}><ExternalLink size={14}/> Abrir</button>{!["READ", "DISMISSED"].includes(item.status) && <button className="button button-small button-ghost" title="Dispensar" onClick={() => void dismiss(item)}><X size={15}/></button>}</div></article>)}</div> : <div className="notification-center-empty"><Bell size={30}/><strong>Nenhum alerta nesta seção.</strong><span>O processamento diário exibirá aqui somente o que precisa de atenção.</span></div>}
    </SectionCard>
  </div>;
}
